<template>
  <MainWrapperDesktop>
    <template #repository="{ isShrunk }">
      <RepositoriesDropdown>
        <template #default="{ repositoryName, repositoryOwner }">
          <DSHeaderRepositorySwitcher
            v-if="repositoryName"
            :name="repositoryName"
            :owner="repositoryOwner"
            :is-shrunk
          />
        </template>
      </RepositoriesDropdown>
      <DSHeaderRefreshButton
        v-if="isRefreshButtonVisible"
        :is-shrunk
        @click="handleRefreshAnalytics"
      />
    </template>
    <template
      v-if="!isReadonly"
      #sidebarTop
    >
      <DSSidebarItem
        label="Add asset"
        :icon="PlusIcon"
        class="w-full justify-center bg-white/10 hover:bg-white/15 border border-white/15"
        @click="handleAddAsset"
      />
    </template>
    <template #sidebar="{ isNavbarOpened }">
      <DSSidebarItem
        v-if="isAdvisorMode"
        label="Advisor"
        :icon="LandmarkIcon"
        :to="{ name: ROUTE_NAME.ADVISOR }"
        :active="route.name === ROUTE_NAME.ADVISOR"
        class="shrink-0"
        :class="isNavbarOpened ? 'w-full h-9.25 justify-start' : undefined"
      />
      <NavbarGroup
        v-for="group in navbarItems"
        :key="group.label"
        :group
        :is-readonly
      />
    </template>
    <template #sidebarFooter>
      <NavbarRepositories />
    </template>
    <template #panel>
      <MainPanelContainer caption="Asset Tree">
        <AssetsTree />
      </MainPanelContainer>
    </template>
    <template #content>
      <AppTabs
        v-if="isTabsVisible"
        :disabled="isEditMode"
        :parent="tabsParent"
        :items="tabsItemsSource"
      >
        <template
          v-if="isAddViewButtonVisible"
          #postfix
        >
          <div class="flex items-center">
            <AddViewButton v-slot="{ icon, disabled: addViewDisabled, label, onClick }">
              <DSButton
                :disabled="addViewDisabled"
                variant="ghost"
                size="sm"
                data-refid="addViewButton"
                @click="onClick"
              >
                <component
                  :is="icon"
                  class="size-3.5 shrink-0"
                  aria-hidden="true"
                />
                {{ label }}
              </DSButton>
            </AddViewButton>
          </div>
        </template>
        <template #actions>
          <LinkDataActions v-if="isLinkedDataActionsVisible" />
        </template>
      </AppTabs>
      <AppAnalyticsProgressBar class="-mx-default" />
      <router-view v-slot="{ Component }">
        <component :is="Component" />
      </router-view>
    </template>
  </MainWrapperDesktop>
</template>

<script setup lang="ts">
import { computed, ComputedRef, inject, markRaw } from 'vue'
import { useRoute } from 'vue-router'

import { NavigationItem, ReadonlyMode } from '@types'

import { ADVISOR_MODE, DASHBOARD_NAME, READONLY_MODE, ROUTE_NAME } from '@/const'

import { useNavigationStore } from '@/store/navigation'
import { useDashboardsStore } from '@/store/dashboards'
import { useModalsStore } from '@/store/modals'

import { useRefreshButton } from './utils/useRefreshButton'

import AddAssetDialog from '@/modules/AddAsset/AddAssetDialog.vue'
import AddViewButton from '@/views/Dashboards/components/AddViewButton.vue'
import LinkDataActions from '@/modules/LinkDataActions/LinkDataActions.vue'

import AssetsTree from './components/AssetsTree.vue'

import MainPanelContainer from './components/MainPanelContainer.vue'
import MainWrapperDesktop from './components/MainWrapperDesktop.vue'
import NavbarGroup from './components/NavbarGroup.vue'
import NavbarRepositories from './components/NavbarRepositories.vue'
import RepositoriesDropdown from './components/RepositoriesDropdown.vue'

import { LandmarkIcon, PlusIcon } from '@lucide/vue'
import { AppAnalyticsProgressBar, AppTabs } from '@app'
import { DSButton, DSHeaderRefreshButton, DSHeaderRepositorySwitcher, DSSidebarItem } from '@ds'

const route = useRoute()

const navigationStore = useNavigationStore()
const dashboardsStore = useDashboardsStore()
const modalsStore = useModalsStore()

const { isRefreshButtonVisible, handleRefreshAnalytics } = useRefreshButton()

const isAdvisorMode = inject<ComputedRef<boolean>>(ADVISOR_MODE)
const isReadonly = inject<ReadonlyMode>(READONLY_MODE)

const isEditMode = computed(() => dashboardsStore.getEditMode)
const isTabsVisible = computed(() => !route.meta.hideTabs)

const navbarItems = computed<NavigationItem[]>(() => navigationStore.getParentNavigation)

const tabsParent = computed(() => navigationStore.getCurrentParent)
const tabsItemsSource = computed(() => navigationStore.getCurrentChildNavigation || [])

const isLinkedDataActionsVisible = computed(() => tabsParent.value?.label === 'Connectors')
const isAddViewButtonVisible = computed(() => tabsParent.value?.label === DASHBOARD_NAME)

const handleAddAsset = () => {
  if (isReadonly?.value) return
  const modalInstance = modalsStore.init('main-add-asset', markRaw(AddAssetDialog))
  modalInstance?.open(modalsStore.getZIndex())
}
</script>
